
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, computed, effect } from '@angular/core';
import { DOCUMENT, DatePipe, CommonModule, DecimalPipe } from '@angular/common';
import { DataService } from '../../services/data.service';
import { AuthService } from '../../services/auth.service';
import { AuditService } from '../../services/audit.service';
import { Customer, Prescription } from '../../models/index';
import { CustomerFormModalComponent } from '../customer-form-modal/customer-form-modal.component';
import { ExportModalComponent, ExportSelection } from '../export-modal/export-modal.component';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../services/notification.service';
import { ExportService } from '../../services/export.service';

type LoadingState = 'loading' | 'loaded' | 'error';
type DetailViewTab = 'medical' | 'identity' | 'history';
type ViewMode = 'reception' | 'table';
type SortKey = 'fullName' | 'createdAt' | 'hn' | 'birthDate';

@Component({
  selector: 'app-customer-management',
  standalone: true,
  templateUrl: './customer-management.component.html',
  styleUrls: ['./customer-management.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CustomerFormModalComponent, ExportModalComponent, DatePipe, DecimalPipe, CommonModule, FormsModule],
  host: {
    '(document:click)': 'onDocumentClick()'
  }
})
export class CustomerManagementComponent implements OnInit {
  private dataService = inject(DataService);
  private authService = inject(AuthService);
  private auditService = inject(AuditService);
  private notificationService = inject(NotificationService);
  private exportService = inject(ExportService);
  private document: Document = inject(DOCUMENT);

  // --- View Control Signals ---
  viewMode = signal<ViewMode>('table');
  searchTerm = signal('');
  sortKey = signal<SortKey>('createdAt');
  sortDirection = signal<'asc' | 'desc'>('desc');
  
  // --- Pagination Signals ---
  currentPage = signal(1);
  pageSize = signal(15);

  // --- Filter Signals ---
  filterAllergiesOnly = signal(false);
  
  // --- Data Signals ---
  customers = signal<Customer[]>([]);
  loadingState = signal<LoadingState>('loading');
  isExporting = signal(false);
  
  // --- Detail View Signals ---
  selectedCustomer = signal<Customer | null>(null);
  activeDetailTab = signal<DetailViewTab>('medical');
  customerHistory = signal<Prescription[]>([]);
  isLoadingHistory = signal(false);

  // --- Modal States ---
  isModalOpen = signal(false);
  isExportModalOpen = signal(false);
  editingCustomer = signal<Customer | null>(null);
  openActionMenuId = signal<string | null>(null);

  // --- COMPUTED: Advanced Data Processing ---
  stats = computed(() => {
    const list = this.customers();
    const today = new Date().toISOString().slice(0, 10);
    return {
      total: list.length,
      newToday: list.filter(c => c.createdAt?.startsWith(today)).length,
      highRisk: list.filter(c => !!c.allergies).length
    };
  });
  
  // Check permission for Export (Owner + Clinic only)
  canExport = computed(() => {
    const user = this.authService.currentUser();
    const store = this.authService.currentStore();
    return user?.role === 'owner' && store?.businessType === 'CLINIC';
  });

  processedCustomers = computed(() => {
    let list = [...this.customers()];
    const term = this.searchTerm().toLowerCase().trim();
    const key = this.sortKey();
    const dir = this.sortDirection();

    if (term) {
      list = list.filter(c => 
        c.fullName.toLowerCase().includes(term) || 
        c.hn?.toLowerCase().includes(term) ||
        c.phone?.includes(term) ||
        c.citizenId?.includes(term)
      );
    }
    
    if (this.filterAllergiesOnly()) {
        list = list.filter(c => !!c.allergies);
    }

    list.sort((a, b) => {
      const valA = (a as any)[key] || '';
      const valB = (b as any)[key] || '';
      const comparison = String(valA).localeCompare(String(valB), undefined, { numeric: true });
      return dir === 'asc' ? comparison : -comparison;
    });

    return list;
  });

  paginatedCustomers = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.processedCustomers().slice(start, start + this.pageSize());
  });

  totalPages = computed(() => Math.ceil(this.processedCustomers().length / this.pageSize()));

  selectedCustomerAge = computed(() => {
      const dob = this.selectedCustomer()?.birthDate;
      if (!dob) return null;
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      return age;
  });

  constructor() {
    effect(() => {
        this.searchTerm(); this.sortKey(); this.sortDirection(); this.filterAllergiesOnly();
        this.currentPage.set(1);
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.loadCustomers();
  }

  async loadCustomers(): Promise<void> {
    this.loadingState.set('loading');
    try {
      const data = await this.dataService.getCustomers();
      this.customers.set(data);
      this.loadingState.set('loaded');
    } catch (e: any) {
      this.loadingState.set('error');
    }
  }

  // --- Actions ---

  toggleAllergyFilter() {
    this.filterAllergiesOnly.update(v => !v);
  }
  
  openExportModal() {
    if (!this.canExport()) {
        this.notificationService.showError('คุณไม่มีสิทธิ์ในการ Export ข้อมูล');
        return;
    }
    this.isExportModalOpen.set(true);
  }

  async handleExportConfirm(selection: ExportSelection) {
    this.isExporting.set(true);
    try {
       let startDate: string | undefined;
       let endDate: string | undefined;

       if (selection.type === 'MONTH') {
           const now = new Date();
           const start = new Date(now.getFullYear(), now.getMonth(), 1);
           // End date should be end of month, or just don't send it to imply 'now'
           startDate = start.toISOString();
       } else if (selection.type === 'CUSTOM') {
           if (selection.startDate) startDate = new Date(selection.startDate).toISOString();
           if (selection.endDate) endDate = new Date(selection.endDate + 'T23:59:59').toISOString();
       }

       console.log('[Export] Requesting data:', { type: selection.type, startDate, endDate });

       const data = await this.dataService.exportCustomers(startDate, endDate);
       
       if (!data || data.length === 0) {
           this.notificationService.showInfo('ไม่พบข้อมูลในช่วงเวลาที่เลือก');
       } else {
           const filename = `Customers_Export_${selection.type}_${new Date().toISOString().split('T')[0]}.csv`;
           this.exportService.exportToCsv(data, filename);
           this.notificationService.showSuccess(`ส่งออกข้อมูลสำเร็จ (${data.length} รายการ)`);
           this.isExportModalOpen.set(false); // Close on success
       }
    } catch(e: any) {
       console.error('[Export] Error:', e);
       this.notificationService.showError(`Export Failed: ${e.message || 'Unknown Error'}`);
    } finally {
       this.isExporting.set(false);
    }
  }

  setSort(key: SortKey) {
      if (this.sortKey() === key) {
          this.sortDirection.update(d => d === 'asc' ? 'desc' : 'asc');
      } else {
          this.sortKey.set(key);
          this.sortDirection.set('asc');
      }
  }

  setPage(p: number) {
      if (p >= 1 && p <= this.totalPages()) {
          this.currentPage.set(p);
      }
  }

  viewCustomerDetails(customer: Customer): void {
    this.selectedCustomer.set(customer);
    this.activeDetailTab.set('medical');
    this.customerHistory.set([]); 
    this.auditService.logViewAccess(`Viewed Patient Record: ${customer.fullName}`, { customer_id: customer.id, hn: customer.hn });
  }

  async setActiveTab(tab: DetailViewTab) {
      this.activeDetailTab.set(tab);
      if (tab === 'history' && this.selectedCustomer()) {
          await this.loadCustomerHistory();
      }
  }

  async loadCustomerHistory() {
      const customer = this.selectedCustomer();
      if (!customer) return;

      this.isLoadingHistory.set(true);
      try {
          const history = await this.dataService.getCustomerHistory(customer.id, 0, 50);
          this.customerHistory.set(history);
      } catch (e) {
          this.notificationService.handleError(e);
      } finally {
          this.isLoadingHistory.set(false);
      }
  }

  reprintPrescription(prescriptionId: string) {
      this.document.defaultView?.open(`/#/print/prescription/${prescriptionId}`, '_blank');
  }

  backToList(): void { 
    this.selectedCustomer.set(null); 
    this.customerHistory.set([]);
  }

  openAddModal(): void { this.editingCustomer.set(null); this.isModalOpen.set(true); }
  openEditModal(customer: Customer): void { this.editingCustomer.set(customer); this.isModalOpen.set(true); this.openActionMenuId.set(null); }
  handleCloseModal(): void { this.isModalOpen.set(false); }
  
  async handleSaveCustomer(data: any): Promise<void> {
    try {
        if (data.id) await this.dataService.updateCustomer(data);
        else await this.dataService.addCustomer(data);
        this.notificationService.showSuccess('บันทึกข้อมูลเรียบร้อยแล้ว');
        this.loadCustomers();
        this.isModalOpen.set(false);
        if (this.selectedCustomer()?.id === data.id) {
            this.selectedCustomer.set(data);
        }
    } catch (e: any) { this.notificationService.handleError(e); }
  }

  onDocumentClick(): void { this.openActionMenuId.set(null); }
}
